import type { ReactNode } from "react";
import { Bell } from "lucide-react";
import { cn } from "@/lib/cn";

interface PortalHeaderProps {
  /** Saudação "Olá, X" — telas internas usam title/subtitle em vez disso. */
  greeting?: string;
  /** Título principal (telas secundárias, ex: "Treinos"). */
  title?: string;
  /** Rótulo cinza acima/abaixo do título. */
  subtitle?: string;
  /** Avatar com inicial (aluno/trainer). */
  avatarInitial?: string;
  /** Logo do trainer (override do avatar com inicial). */
  avatarUrl?: string | null;
  /** Slot opcional à direita (default: sino de notificações). */
  rightSlot?: ReactNode;
}

/**
 * Header do portal mobile-first. Não é sticky por design — em mobile, ele
 * scrolla junto com o conteúdo pra liberar espaço vertical na tela pequena.
 * Portado de `frontend/components/portal/PortalHeader.tsx` (Next) → classes do
 * tema novo (bg-elevated, text-faint, font-display…) e sem `next/link`.
 */
export function PortalHeader({
  greeting,
  title,
  subtitle,
  avatarInitial,
  avatarUrl,
  rightSlot,
}: PortalHeaderProps) {
  return (
    <header className="px-5 pt-6 pb-3">
      <div className="flex items-center gap-3">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt="Avatar"
            className="h-11 w-11 shrink-0 rounded-full border border-border-subtle object-cover"
          />
        ) : avatarInitial ? (
          <div
            aria-hidden
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border-subtle bg-elevated font-display text-lg text-accent-soft"
          >
            {avatarInitial}
          </div>
        ) : null}

        <div className="min-w-0 flex-1">
          {greeting ? (
            <>
              <p className="text-[11px] font-medium uppercase tracking-wider text-faint">
                Bem-vindo de volta
              </p>
              <h1 className="truncate font-display text-xl text-ink">{greeting}</h1>
            </>
          ) : (
            <>
              {subtitle ? (
                <p className="text-[11px] font-medium uppercase tracking-wider text-faint">
                  {subtitle}
                </p>
              ) : null}
              {title ? (
                <h1 className="truncate font-display text-xl text-ink">{title}</h1>
              ) : null}
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-1.5">
          {rightSlot ?? (
            <button
              type="button"
              aria-label="Notificações"
              className={cn(
                "inline-flex h-10 w-10 items-center justify-center rounded-full border border-border-subtle bg-elevated text-muted transition-colors",
                "hover:border-border-strong hover:text-ink",
              )}
            >
              <Bell size={17} aria-hidden />
            </button>
          )}
        </div>
      </div>
    </header>
  );
}
